import { oc } from "@orpc/contract";
import { z } from "zod";
import {
  USER_FEEDBACK_DATA_TYPES,
  USER_FEEDBACK_SOURCES,
  USER_FEEDBACK_SUBJECT_TYPES,
} from "@erp/shared-types";

// ── Shared schemas ────────────────────────────────────────────────────

export const FeedbackDataTypeSchema = z.enum(USER_FEEDBACK_DATA_TYPES);
export const FeedbackSourceSchema = z.enum(USER_FEEDBACK_SOURCES);
export const FeedbackSubjectTypeSchema = z.enum(USER_FEEDBACK_SUBJECT_TYPES);

export const FeedbackCategoricalOptionSchema = z.object({
  value: z.string(),
  label: z.string(),
});

export const FeedbackValueSchema = z.object({
  dataType: FeedbackDataTypeSchema,
  numericValue: z.number().nullable().optional(),
  categoricalValue: z.string().nullable().optional(),
  booleanValue: z.boolean().nullable().optional(),
});

export const FeedbackRecordSchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  createdByUserId: z.uuid().nullable(),
  traceId: z.string().nullable(),
  observationId: z.string().nullable(),
  subjectType: FeedbackSubjectTypeSchema,
  subjectId: z.string(),
  scoreName: z.string(),
  dataType: FeedbackDataTypeSchema,
  numericValue: z.number().nullable(),
  categoricalValue: z.string().nullable(),
  booleanValue: z.boolean().nullable(),
  comment: z.string().nullable(),
  source: FeedbackSourceSchema,
  metadata: z.record(z.string(), z.unknown()).nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const ListFeedbackInputSchema = z.object({
  subjectType: FeedbackSubjectTypeSchema.optional(),
  limit: z.coerce.number().int().min(1).max(100).prefault(25),
  offset: z.coerce.number().int().min(0).prefault(0),
});

export const ListFeedbackResponseSchema = z.object({
  feedback: z.array(FeedbackRecordSchema),
  pagination: z.object({
    total: z.number(),
    limit: z.number(),
    offset: z.number(),
  }),
});

export const FeedbackConfigSchema = z.object({
  subjectType: FeedbackSubjectTypeSchema,
  scoreName: z.string(),
  label: z.string(),
  description: z.string().nullable(),
  dataType: FeedbackDataTypeSchema,
  numericMin: z.number().nullable(),
  numericMax: z.number().nullable(),
  numericStep: z.number().nullable(),
  categoricalOptions: z.array(FeedbackCategoricalOptionSchema).nullable(),
  commentEnabled: z.boolean(),
  commentRequired: z.boolean(),
  sortOrder: z.number(),
});

// ── Contract ──────────────────────────────────────────────────────────

export const userFeedbackContracts = {
  /**
   * Upsert this user's feedback for a given (subject, scoreName).
   */
  submit: oc
    .input(
      z.object({
        subjectType: FeedbackSubjectTypeSchema,
        subjectId: z.string(),
        scoreName: z.string(),
        value: FeedbackValueSchema,
        comment: z.string().max(5000).nullable().optional(),
        traceId: z.string().nullable().optional(),
        // Langfuse observation (span) id within the trace. When provided,
        // the score is attached to that specific observation rather than
        // to the whole trace. Persisted on the feedback row so re-submits
        // after a page reload retain the correct observation linkage.
        observationId: z.string().nullable().optional(),
      }),
    )
    .output(z.object({ feedback: FeedbackRecordSchema }))
    .route({ method: "POST" }),

  /**
   * Soft-delete a feedback row. Caller must be the original author.
   */
  delete: oc
    .input(z.object({ feedbackId: z.uuid() }))
    .output(z.object({ success: z.boolean() }))
    .route({ method: "POST" }),

  /**
   * Get the calling user's feedback for a subject, keyed by scoreName.
   * Returns ONLY the caller's rows; no aggregate, no other users.
   */
  getMyForSubject: oc
    .input(
      z.object({
        subjectType: FeedbackSubjectTypeSchema,
        subjectId: z.string(),
      }),
    )
    .output(
      z.object({
        feedbackByScoreName: z.record(z.string(), FeedbackRecordSchema),
      }),
    )
    .route({ method: "GET" }),

  /**
   * List the calling user's own feedback, newest first, paginated.
   * Always scoped to the current user server-side (there is no user field on
   * the input) — the whole input defaults, so `listFeedback()` returns your
   * recent feedback across all subject types.
   */
  listFeedback: oc
    .input(ListFeedbackInputSchema)
    .output(ListFeedbackResponseSchema)
    .route({ method: "GET" }),

  /**
   * Get active feedback configs for a subject type, sorted by sortOrder.
   */
  getConfigs: oc
    .input(z.object({ subjectType: FeedbackSubjectTypeSchema }))
    .output(z.object({ configs: z.array(FeedbackConfigSchema) }))
    .route({ method: "GET" }),
} as const;

// ── Type exports ──────────────────────────────────────────────────────

export type FeedbackRecord = z.infer<typeof FeedbackRecordSchema>;
export type ListFeedbackInput = z.infer<typeof ListFeedbackInputSchema>;
export type ListFeedbackResponse = z.infer<typeof ListFeedbackResponseSchema>;
export type FeedbackConfig = z.infer<typeof FeedbackConfigSchema>;
export type FeedbackValue = z.infer<typeof FeedbackValueSchema>;
export type FeedbackCategoricalOption = z.infer<
  typeof FeedbackCategoricalOptionSchema
>;
export type FeedbackDataType = z.infer<typeof FeedbackDataTypeSchema>;
export type FeedbackSource = z.infer<typeof FeedbackSourceSchema>;
export type FeedbackSubjectType = z.infer<typeof FeedbackSubjectTypeSchema>;
